import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { HostApiService } from 'lib-host-interface';
import { delay, Observable, of } from 'rxjs';

export const mockCompanyInfo = {
  firstName: 'Amy',
  middleName: null,
  lastName: 'Bishop',
  crdFirmId: '3030',
  userId: 'BishopA',
  email: 'bishopa@finra.org',
  phoneNumber: '',
  faxNumber: null,
  isAdmin: null,
  firmName: 'Rocket Mortgage, LLC',
  firm: {
    crdFirmId: '3030',
    firmName: 'Rocket Mortgage, LLC',
    isBDFirm: null,
    isIAFirm: null,
    isFPFirm: null,
    isNMFirm: null,
    __typename: 'Firm',
  },
  org: {
    orgSize: null,
    __typename: 'Org',
  },
  __typename: 'UserProfile',
};

export const mockIndividualInfo = {
  __typename: 'UserProfile',
  crdFirmId: '789012',
  email: 'michael.chen@individual.com',
  faxNumber: null,
  firm: {
    __typename: 'Firm',
    crdFirmId: '789012',
    firmName: 'MICHAEL CHEN',
    isBDFirm: null,
    isIAFirm: null,
  },
  firmName: 'MICHAEL CHEN',
  firstName: 'MICHAEL',
  isAdmin: null,
  lastName: 'CHEN',
  middleName: null,
  org: {
    __typename: 'Org',
    orgSize: null,
  },
  phoneNumber: '415-555-0456',
  userId: 'CHENM',
};

// Function to get user type dynamically

function getUserType(): 'Company' | 'Individual' | 'Regulator' {
  if (typeof window !== 'undefined') {
    // Check URL parameters first (for manual testing)
    const urlParams = new URLSearchParams(window.location.search);
    const userTypeFromUrl = urlParams.get('userType') as 'Company' | 'Individual' | 'Regulator';
    if (userTypeFromUrl && ['Company', 'Individual', 'Regulator'].includes(userTypeFromUrl)) {
      console.log('🔍 User type from URL:', userTypeFromUrl);
      return userTypeFromUrl;
    }

    // Check cookies with multiple approaches
    const cookies = document.cookie;
    console.log('🔍 All cookies:', cookies);

    // Try different cookie patterns
    const patterns = [
      /userType=([^;]+)/,
      /userType%3D([^;]+)/, // URL encoded
      /userType%253D([^;]+)/, // Double URL encoded
    ];

    for (const pattern of patterns) {
      const userTypeMatch = cookies.match(pattern);
      if (userTypeMatch) {
        const userType = decodeURIComponent(userTypeMatch[1]) as 'Company' | 'Individual' | 'Regulator';
        if (['Company', 'Individual', 'Regulator'].includes(userType)) {
          console.log('🔍 User type from cookie:', userType, 'pattern:', pattern);
          return userType;
        }
      }
    }

    // Check localStorage as fallback
    try {
      const storedUserType = localStorage.getItem('userType') as 'Company' | 'Individual' | 'Regulator';
      if (storedUserType && ['Company', 'Individual', 'Regulator'].includes(storedUserType)) {
        console.log('🔍 User type from localStorage:', storedUserType);
        return storedUserType;
      }
    } catch (e) {
      console.warn('Could not access localStorage:', e);
    }

    // Check sessionStorage as fallback
    try {
      const sessionUserType = sessionStorage.getItem('userType') as 'Company' | 'Individual' | 'Regulator';
      if (sessionUserType && ['Company', 'Individual', 'Regulator'].includes(sessionUserType)) {
        console.log('🔍 User type from sessionStorage:', sessionUserType);
        return sessionUserType;
      }
    } catch (e) {
      console.warn('Could not access sessionStorage:', e);
    }
  }

  // Default fallback
  console.log('🔍 Using default user type: Company');
  return 'Company';
}

export const mockRegulatorInfo = {
  __typename: 'UserProfile',
  crdFirmId: 'REG001',
  email: 'regulator@finra.org',
  faxNumber: null,
  firm: {
    __typename: 'Firm',
    crdFirmId: 'REG001',
    firmName: 'FINRA Regulatory',
    isBDFirm: null,
    isIAFirm: null,
  },
  firmName: 'FINRA Regulatory',
  firstName: 'JANE',
  isAdmin: true,
  lastName: 'REGULATOR',
  middleName: null,
  org: {
    __typename: 'Org',
    orgSize: null,
  },
  phoneNumber: '202-555-0123',
  userId: 'REGULATORJ',
};

// Function to get mock data based on user type
function getMockDataForUserType(userType: 'Company' | 'Individual' | 'Regulator') {
  const classification = userType;
  let userInfo;

  if (userType === 'Company') {
    userInfo = mockCompanyInfo;
  } else if (userType === 'Individual') {
    userInfo = mockIndividualInfo;
  } else {
    userInfo = mockRegulatorInfo;
  }

  console.log('🏷️ Mock classification set to:', classification);

  return {
    classification,
    userInfo,
  };
}

// Get current user type and update mock data
export const mockAppHostUser = {
  info: mockCompanyInfo, // This will be overridden dynamically
  profile: {
    tmp: 'test',
  },
  preferences: {
    startingRoute: 'test',
    language: 'test',
  },
  setLanguage: () => {},
};

export const mockCompanyEntitlements = [
  'MU1',
  'MU1R',
  'IndividualTestEnrollment',
  'IndividualManageCompanyRelationships',
  'ViewIndividual',
  'CompanyAccess',
  'ConfidentialInfo',
  'ViewTestEducation',
  'IndividualQueues',
  'IndividualRenewals',
  'Administrator',
];

export const mockIndividualEntitlements = [
  'MU1',
  'MU1R',
  'IndividualTestEnrollment',
  'ViewIndividual',
  'ConfidentialInfo',
  'ViewTestEducation',
];

export const mockRegulatorEntitlements = ['Administrator', 'ConfidentialInfo', 'RegulatoryAccess', 'RegulatorWorkItem'];

// Function to get permissions based on user type
function getPermissionsForUserType(userType: 'Company' | 'Individual' | 'Regulator') {
  let permissions;

  if (userType === 'Company') {
    permissions = mockCompanyEntitlements;
  } else if (userType === 'Individual') {
    permissions = mockIndividualEntitlements;
  } else {
    permissions = mockRegulatorEntitlements;
  }

  console.log('🔐 Mock permissions set for', userType, ':', permissions);
  return permissions;
}

@Injectable({
  providedIn: 'root',
})
export class hostApiMockService extends HostApiService {
  constructor(@Inject(DOCUMENT) document: Document) {
    super(document);
  }

  user = {
    info: of(this.getMockUserInfo()).pipe(delay(1000)),
  } as any;

  entitlements = {
    classification: of(this.getUserClassification()).pipe(delay(1300)),
    permissions: of(this.getUserPermissions()).pipe(delay(1300)),
  } as any;

  private getMockUserInfo() {
    const userType = getUserType();
    const mockData = getMockDataForUserType(userType);
    return mockData.userInfo;
  }

  private getUserClassification() {
    const userType = getUserType();
    console.log('🏷️ Mock classification set to:', userType);
    return userType;
  }

  private getUserPermissions() {
    const userType = getUserType();
    const permissions = getPermissionsForUserType(userType);
    return permissions;
  }
}
